import type { LoaderFunctionArgs } from 'react-router-dom';
import { contracts, type CompanyMailboxView } from '@immo/api-contract';
import { apiClient } from '@/lib/auth/api-client';
import { loadWorkspaceContext } from '@/lib/auth/workspace-context';
import { mayCall } from '@/lib/permissions/route-gate';

export type MailboxHomeLoaderData = {
  readonly workspaceId: string;
  readonly workspaceSlug: string;
  readonly canConnectMailbox: boolean;
  readonly canSendMailbox: boolean;
  readonly canWriteActivity: boolean;
  readonly companyMailboxes: ReadonlyArray<CompanyMailboxView>;
};

// The standalone mailbox home (IMM-233.3). Reads the workspace + the caller's
// mailbox capabilities up front, so the page can gate the connect empty state
// (mailbox-connect) and the compose/reply controls (mailbox-send) the same way the
// contact timeline does.
export async function mailboxHomeLoader({ params }: LoaderFunctionArgs): Promise<MailboxHomeLoaderData> {
  const { workspaceId, workspaceSlug, permissions } = await loadWorkspaceContext(params.workspaceId);

  // Best-effort: the personal inbox must never fail because the company list could not load.
  const companyMailboxes = mayCall(permissions, contracts.companyMailboxes.list)
    ? await apiClient.companyMailboxes
        .list({ pathParams: { workspaceId } })
        .then((result) => (result.ok ? result.body.items : []))
        .catch(() => [])
    : [];

  return {
    workspaceId,
    workspaceSlug,
    canConnectMailbox: mayCall(permissions, contracts.mailbox.connect),
    canSendMailbox: mayCall(permissions, contracts.mailbox.send),
    canWriteActivity: mayCall(permissions, contracts.activities.record),
    companyMailboxes,
  };
}
